import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { sk } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchAdminTenantById, setAdminTenantBlocked, extendAdminTenantLicense, deleteAdminManualPayment, setAdminTenantOwner } from '../../services/adminApi';

const TYPE_LABELS = {
    company: 'Spoločnosť',
    school: 'Škola',
    town: 'Obec',
    transporter: 'Prepravca',
    'recycling center': 'Zberný dvor',
};

const STATUS_META = {
    trialing:  { label: 'Skúšobné',     color: 'yellow' },
    active:    { label: 'Aktívne',       color: 'green'  },
    past_due:  { label: 'Po splatnosti', color: 'red'    },
    canceled:  { label: 'Zrušené',       color: 'gray'   },
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

const formatAmount = (cents) => {
    if (!cents) return '—';
    return `${(cents / 100).toFixed(2)} €`;
};

const PAYMENT_STATUS_META = {
    succeeded: { label: 'Úspešná',   color: 'green'  },
    failed:    { label: 'Neúspešná', color: 'red'    },
    pending:   { label: 'Čaká',      color: 'yellow' },
};

const MODULE_LABELS = { waste: 'Evidencia odpadov', ppwr: 'PPWR obaly' };
const MODULE_LABELS_SHORT = { waste: 'Odpady', ppwr: 'PPWR' };
const BILLABLE_MODULES = Object.keys(MODULE_LABELS);

const PLAN_LABELS_BY_MODULE = {
    waste: { demo: 'Demo', start: 'Start', business: 'Business' },
    ppwr: { demo: 'Demo', S: 'S', M: 'M', L: 'L' },
};

const EXTEND_PLAN_OPTIONS = {
    waste: [{ value: 'start', label: 'Start' }, { value: 'business', label: 'Business' }],
    ppwr: [
        { value: 'demo', label: 'Demo' },
        { value: 'S', label: 'S' },
        { value: 'M', label: 'M' },
        { value: 'L', label: 'L' },
    ],
};

const TABS = [
    { key: 'subscriptions', label: 'Predplatné' },
    { key: 'payments', label: 'Platby' },
    { key: 'members', label: 'Používatelia' },
];

const planLabel = (module, plan) => PLAN_LABELS_BY_MODULE[module]?.[plan] ?? plan;

const defaultExtendPlan = (module, currentPlan) => {
    if (module === 'waste') return (!currentPlan || currentPlan === 'demo') ? 'start' : currentPlan;
    if (module === 'ppwr') return (currentPlan && currentPlan !== 'demo') ? currentPlan : 'S';
    return '';
};

function Row({ label, value, mono }) {
    return (
        <div className="adm-detail-row">
            <span className="adm-detail-label">{label}</span>
            <span className={`adm-detail-value${mono ? ' adm-td-mono' : ''}`}>{value ?? '—'}</span>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="adm-detail-section">
            <div className="adm-detail-section-title">{title}</div>
            {children}
        </div>
    );
}

function ModuleExtendForm({ module, currentPlan, hasSubscription, onSubmit }) {
    const [plan, setPlan] = useState(defaultExtendPlan(module, currentPlan));
    const [mode, setMode] = useState('months');
    const [months, setMonths] = useState(12);
    const [date, setDate] = useState(null);
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('invoice');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async () => {
        if (mode === 'date' && !date) {
            setError('Vyber dátum.');
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const amountEur = amount !== '' ? parseFloat(amount) : null;
            await onSubmit({
                module,
                plan,
                paymentMethod,
                ...(mode === 'date'
                    ? { endDate: date.toISOString() }
                    : { months }),
                ...(amountEur != null && !isNaN(amountEur) && { amountEur }),
            });
        } catch {
            setError('Akciu sa nepodarilo vykonať.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="adm-module-extend">
            <div className="adm-extend-toggle">
                <button
                    className={`adm-extend-tab${mode === 'months' ? ' adm-extend-tab--active' : ''}`}
                    onClick={() => setMode('months')}
                >
                    Počet mesiacov
                </button>
                <button
                    className={`adm-extend-tab${mode === 'date' ? ' adm-extend-tab--active' : ''}`}
                    onClick={() => setMode('date')}
                >
                    Konkrétny dátum
                </button>
            </div>

            <div className="adm-extend-row">
                <select
                    className="adm-filter-select adm-extend-select"
                    value={plan}
                    onChange={e => setPlan(e.target.value)}
                >
                    {EXTEND_PLAN_OPTIONS[module].map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    className="adm-filter-select adm-extend-select"
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                >
                    <option value="invoice">F</option>
                    <option value="cash">H</option>
                </select>
                <div className="adm-extend-amount-wrap">
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="adm-filter-input adm-extend-amount"
                        placeholder="Suma (€) — nepovinné"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                    />
                </div>
            </div>

            <div className="adm-extend-row">
                {mode === 'months' ? (
                    <select
                        className="adm-filter-select adm-extend-select"
                        value={months}
                        onChange={e => setMonths(Number(e.target.value))}
                    >
                        <option value={1}>1 mesiac</option>
                        <option value={3}>3 mesiace</option>
                        <option value={6}>6 mesiacov</option>
                        <option value={12}>12 mesiacov</option>
                        <option value={24}>24 mesiacov</option>
                    </select>
                ) : (
                    <DatePicker
                        selected={date}
                        onChange={d => setDate(d)}
                        dateFormat="dd.MM.yyyy"
                        locale={sk}
                        placeholderText="Vyber dátum"
                        className="adm-datepicker-input"
                        popperPlacement="top-start"
                    />
                )}

                <button
                    className="adm-extend-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting
                        ? <span className="adm-btn-spinner" />
                        : hasSubscription ? 'Predĺžiť' : 'Aktivovať'}
                </button>
            </div>
            {error && <p className="adm-extend-error">{error}</p>}
        </div>
    );
}

export default function TenantDetail({ tenantId, onClose, onUpdate }) {
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [blocking, setBlocking] = useState(false);
    const [activeTab, setActiveTab] = useState('subscriptions');
    const [settingOwnerId, setSettingOwnerId] = useState(null);

    useEffect(() => {
        if (!tenantId) return;
        setLoading(true);
        setError(null);
        setActiveTab('subscriptions');
        fetchAdminTenantById(tenantId)
            .then(res => setTenant(res.data))
            .catch(() => setError('Nepodarilo sa načítať detail tenanta.'))
            .finally(() => setLoading(false));
    }, [tenantId]);

    const reloadTenant = () =>
        fetchAdminTenantById(tenantId).then(res => setTenant(res.data));

    const handleDeletePayment = async (paymentId) => {
        if (!window.confirm('Naozaj chcete vymazať túto platbu?')) return;
        try {
            await deleteAdminManualPayment(tenantId, paymentId);
            await reloadTenant();
            onUpdate?.();
        } catch {
            alert('Platbu sa nepodarilo vymazať.');
        }
    };

    const handleExtend = async (payload) => {
        await extendAdminTenantLicense(tenantId, payload);
        await reloadTenant();
        onUpdate?.();
    };

    const handleSetOwner = async (member) => {
        if (!window.confirm(`Naozaj chcete nastaviť používateľa ${member.name} ${member.surname} ako owner-a tohto tenanta?`)) return;
        setSettingOwnerId(member._id);
        try {
            await setAdminTenantOwner(tenantId, member._id);
            await reloadTenant();
            onUpdate?.();
        } catch {
            alert('Owner-a sa nepodarilo nastaviť.');
        } finally {
            setSettingOwnerId(null);
        }
    };

    const handleToggleBlock = async () => {
        const next = !tenant.blocked;
        const label = next ? 'zablokovať' : 'odblokovať';
        if (!window.confirm(`Naozaj chcete ${label} tohto tenanta?`)) return;
        setBlocking(true);
        try {
            await setAdminTenantBlocked(tenantId, next);
            setTenant(t => ({ ...t, blocked: next }));
            onUpdate?.();
        } catch {
            alert('Akciu sa nepodarilo vykonať.');
        } finally {
            setBlocking(false);
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <>
            <div className="adm-drawer-backdrop" onClick={onClose} />
            <aside className="adm-drawer">
                <div className="adm-drawer-header">
                    <div>
                        {loading || !tenant ? (
                            <div className="adm-drawer-title">Načítavam...</div>
                        ) : (
                            <>
                                <div className="adm-drawer-title">{tenant.companyName}</div>
                                <div className="adm-drawer-subtitle">{TYPE_LABELS[tenant.type] ?? tenant.type}</div>
                            </>
                        )}
                    </div>
                    <button className="adm-drawer-close" onClick={onClose} aria-label="Zavrieť">✕</button>
                </div>

                <div className="adm-drawer-main">
                    {loading && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0', width: '100%' }}>
                            <span className="adm-spinner" />
                        </div>
                    )}

                    {error && <div className="adm-error" style={{ margin: 24 }}>{error}</div>}

                    {!loading && tenant && (
                        <>
                            <aside className="adm-drawer-sidebar">
                                <Section title="Kontaktné údaje">
                                    <Row label="E-mail" value={tenant.email} />
                                    <Row label="Telefón" value={tenant.contact} />
                                    <Row label="Zodpovedná osoba" value={tenant.responsiblePerson} />
                                </Section>

                                <Section title="Fakturačné údaje">
                                    <Row label="IČO" value={tenant.identificationNumber} mono />
                                    <Row label="DIČ" value={tenant.vatId} mono />
                                    <Row label="IČ DPH" value={tenant.vatRegNo || '—'} mono />
                                    <Row label="Ulica" value={tenant.address?.street} />
                                    <Row label="Mesto" value={`${tenant.address?.zip ?? ''} ${tenant.address?.city ?? ''}`.trim()} />
                                </Section>

                                <Section title="Štatistiky">
                                    <Row label="Celková hodnota" value={formatAmount(tenant.totalValue)} />
                                    <Row label="Počet pobočiek" value={tenant.branchCount} />
                                    <Row label="Počet členov" value={tenant.memberCount} />
                                    <Row label="Evidenčné listy" value={tenant.evidenceSheetCount} />
                                    <Row label="Blokovaný" value={tenant.blocked ? 'Áno' : 'Nie'} />
                                </Section>

                                <div className="adm-drawer-sidebar-footer">
                                    <button
                                        className={`adm-block-btn${tenant.blocked ? ' adm-block-btn--unblock' : ''}`}
                                        onClick={handleToggleBlock}
                                        disabled={blocking}
                                    >
                                        {blocking
                                            ? <span className="adm-btn-spinner" />
                                            : tenant.blocked ? 'Odblokovať tenanta' : 'Zablokovať tenanta'
                                        }
                                    </button>
                                </div>
                            </aside>

                            <div className="adm-drawer-content">
                                <div className="adm-tabs">
                                    {TABS.map(tab => (
                                        <button
                                            key={tab.key}
                                            className={`adm-tab${activeTab === tab.key ? ' adm-tab--active' : ''}`}
                                            onClick={() => setActiveTab(tab.key)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="adm-tab-body">
                                    {activeTab === 'subscriptions' && BILLABLE_MODULES.map(module => {
                                        const sub = tenant.subscriptions?.[module];
                                        const status = sub
                                            ? (STATUS_META[sub.status] ?? { label: sub.status, color: 'gray' })
                                            : null;
                                        const licenseEnd = sub?.currentPeriodEnd ?? sub?.trialEndsAt ?? null;
                                        const isExpired = licenseEnd && new Date(licenseEnd) < new Date();
                                        const currentPlan = module === 'waste' ? tenant.plan : tenant.ppwrPlan;
                                        return (
                                            <div key={module} className="adm-module-card">
                                                <div className="adm-detail-subtitle">{MODULE_LABELS[module]}</div>
                                                {sub ? (
                                                    <>
                                                        <Row label="Plán" value={planLabel(module, sub.plan)} />
                                                        <Row label="Stav" value={
                                                            <span className={`adm-badge adm-badge--${status.color}`}>{status.label}</span>
                                                        } />
                                                        <Row label="Platné od" value={formatDate(sub.currentPeriodStart)} />
                                                        <Row label="Platné do" value={
                                                            <span style={isExpired ? { color: 'var(--adm-red)' } : {}}>
                                                                {formatDate(licenseEnd)}
                                                            </span>
                                                        } />
                                                        <Row label="Posledná platba" value={formatDate(sub.lastPaymentAt)} />
                                                        <Row label="Suma" value={formatAmount(sub.lastPaymentAmount)} />
                                                    </>
                                                ) : (
                                                    <p className="adm-detail-empty">Balík nie je aktivovaný.</p>
                                                )}
                                                <ModuleExtendForm
                                                    module={module}
                                                    currentPlan={currentPlan}
                                                    hasSubscription={!!sub}
                                                    onSubmit={handleExtend}
                                                />
                                            </div>
                                        );
                                    })}

                                    {activeTab === 'members' && (
                                        !tenant.members?.length ? (
                                            <p className="adm-detail-empty">Žiadni používatelia.</p>
                                        ) : (
                                            <div className="adm-member-list">
                                                {tenant.members.map(m => (
                                                    <div key={m._id} className="adm-member-row">
                                                        <div className="adm-member-avatar">
                                                            {(m.name?.[0] ?? '') + (m.surname?.[0] ?? '')}
                                                        </div>
                                                        <div className="adm-member-info">
                                                            <span className="adm-member-name">
                                                                {m.name} {m.surname}
                                                                {m.isOwner && <span className="adm-badge adm-badge--purple" style={{ marginLeft: 6 }}>Owner</span>}
                                                            </span>
                                                            <span className="adm-member-email">{m.email}</span>
                                                        </div>
                                                        {!m.isOwner && (
                                                            <button
                                                                className="adm-member-set-owner"
                                                                onClick={() => handleSetOwner(m)}
                                                                disabled={settingOwnerId === m._id}
                                                            >
                                                                {settingOwnerId === m._id
                                                                    ? <span className="adm-btn-spinner" />
                                                                    : 'Nastaviť ako owner'
                                                                }
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )
                                    )}

                                    {activeTab === 'payments' && (
                                        !tenant.payments?.length ? (
                                            <p className="adm-detail-empty">Žiadne platby.</p>
                                        ) : (
                                            <div className="adm-payment-list">
                                                {tenant.payments.map(p => {
                                                    const ps = PAYMENT_STATUS_META[p.status] ?? { label: p.status, color: 'gray' };
                                                    const METHOD_BADGE = { invoice: 'F', cash: 'H' };
                                                    const isManual = p.stripePaymentIntentId?.startsWith('manual_');
                                                    const methodLabel = p.paymentMethod && p.paymentMethod !== 'stripe'
                                                        ? METHOD_BADGE[p.paymentMethod]
                                                        : null;
                                                    return (
                                                        <div key={p._id} className="adm-payment-row">
                                                            <div className="adm-payment-left">
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                    <span className="adm-badge adm-badge--gray">{MODULE_LABELS_SHORT[p.module] ?? p.module ?? 'Odpady'}</span>
                                                                    <span className="adm-payment-plan">{planLabel(p.module ?? 'waste', p.plan)}</span>
                                                                    {isManual && <span className="adm-badge adm-badge--gray">Manuálne</span>}
                                                                    {methodLabel && <span className="adm-badge adm-badge--gray adm-method-badge">{methodLabel}</span>}
                                                                </div>
                                                                <span className="adm-payment-date">{formatDate(p.createdAt)}</span>
                                                                {p.superfakturaInvoiceNo && (
                                                                    <span className="adm-payment-invoice">Faktúra č. {p.superfakturaInvoiceNo}</span>
                                                                )}
                                                                {(p.periodStart || p.periodEnd) && (
                                                                    <span className="adm-payment-period">
                                                                        {formatDate(p.periodStart)} – {formatDate(p.periodEnd)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="adm-payment-right">
                                                                <span className="adm-payment-amount">{formatAmount(p.amount)}</span>
                                                                <span className={`adm-badge adm-badge--${ps.color}`}>{ps.label}</span>
                                                                {isManual && (
                                                                    <button
                                                                        className="adm-payment-delete"
                                                                        onClick={() => handleDeletePayment(p._id)}
                                                                        title="Vymazať platbu"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
